// Reading a rejected API call the way the cashier needs it read.
//
// POST /transactions/ rejects an incomplete modifier selection with 400 and a
// `detail` that is an ARRAY of human-readable group labels
// (backend/app/routers/transactions.py — e.g. ["Flavour", "Corn → Corn
// flavour"]). Every OTHER 400 on that path returns a plain string. Axios
// itself only ever gives us "Request failed with status code 400", which at a
// till, mid-service, with a customer waiting, tells the cashier nothing they
// can act on. This pulls the list out so the call site can name the groups
// that still need answering.
//
// Pure and dependency-free on purpose: it is the one bit of "what does this
// error mean" logic shared by every payment call site, and it is worth being
// able to test it without a network, a store, or a render.

/**
 * The modifier-selection problem list carried by a rejected request, or null.
 *
 * Returns null — never throws, never a partial — for anything that is not
 * that specific shape: a string `detail` (every other 400), a network error
 * with no response at all, an empty list, or FastAPI's own 422 validation
 * shape (an array of objects rather than strings). A null answer means
 * "nothing better to say than the generic message", which is the caller's
 * existing behaviour, so this can only ever add information.
 *
 * @param {unknown} error - the value a rejected api call threw.
 * @returns {string[] | null}
 */
export const selectionProblemsFrom = (error) => {
    const detail = error?.response?.data?.detail;
    if (!Array.isArray(detail)) return null;

    const problems = detail
        .filter((entry) => typeof entry === 'string')
        .map((entry) => entry.trim())
        .filter(Boolean);

    return problems.length ? problems : null;
};

/**
 * The sentence the server sent, for rejections that are not a selection list.
 *
 * The payment call sites fall back to `err.message` when
 * ``selectionProblemsFrom`` finds nothing — and for any axios rejection that
 * is "Request failed with status code 400", which is what the QC screenshot
 * shows and is unactionable at a till. Meanwhile the body carried something
 * the cashier could act on: "Sales are not allowed. Please start the business
 * day first." for the other 400 on POST /transactions/, or the support
 * reference in a 500 (see main.py's catch_unhandled_exceptions).
 *
 * Only a STRING `detail` counts. An array is a selection problem list and
 * belongs to ``selectionProblemsFrom``; an object is FastAPI's 422 shape; a
 * string BODY (rather than a string `detail`) is a proxy's HTML error page,
 * which is worse than saying nothing. Returns null in all of those cases, so
 * the caller keeps its existing `err.message` fallback.
 *
 * @param {unknown} error - the value a rejected api call threw.
 * @returns {string | null}
 */
export const serverMessageFrom = (error) => {
    const detail = error?.response?.data?.detail;
    if (typeof detail !== 'string') return null;
    const message = detail.trim();
    return message || null;
};

export default selectionProblemsFrom;
